import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Card, ServerPage } from "@/components/server-shell";
import { Chip, errorMessage, Select } from "@/components/ui-bits";
import { useAgentQuery } from "@/lib/act";
import { KIND, money, type RunRow, type RunsList, took, when } from "@/lib/runs";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/$id/activity")({
  validateSearch: z.object({ mode: z.string().optional(), status: z.string().optional(), before: z.number().optional() }),
  component: Activity,
});

const th = "px-5 py-3 font-medium";
const td = "px-5 py-3";
const control = "h-10 bg-[#1A1A19]";

const num = (n: number) => n.toLocaleString("en-US");

/** Applied changes, else proposed ones marked "?"; nothing yet while the run is still going. */
function changes(r: RunRow) {
  if (r.status === "running" && !r.applied && !r.proposed) return "—";
  const n = r.applied || r.proposed;
  return n ? `${n}${r.applied ? "" : "?"}` : "0";
}

/** Failed runs read red; runs that changed nothing (or haven't finished) read muted. */
function summaryTone(r: RunRow) {
  if (r.status === "failed") return "text-[#F48A80]";
  if (r.status === "running" || (!r.applied && !r.proposed)) return "text-[#A8A59D]";
  return "";
}

function Activity() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const connected = !!useServer(id).data?.hub.connected;
  const q = useAgentQuery<RunsList>(id, "runs_list", { mode: search.mode, status: search.status, before: search.before }, connected);
  const [find, setFind] = useState("");
  const runCycle = useMutation({
    mutationFn: () => client.servers.runCycle({ id }),
    onSuccess: () => toast.success("Cycle started"),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const set = (k: "mode" | "status", v: string) => navigate({ search: { ...search, [k]: v || undefined, before: undefined } });
  const go = () => {
    const v = find.trim();
    if (/^\d+$/.test(v)) navigate({ to: "/servers/$id/runs/$rid", params: { id, rid: v } });
    else if (/^[A-Za-z][A-Za-z0-9]*-\d+$/.test(v)) navigate({ to: "/servers/$id/tasks/$key", params: { id, key: v.toUpperCase() } });
    else toast.error("Enter a run number or a task key such as ACME-14");
  };
  const s = q.data?.stats;

  return (
    <ServerPage
      title="Activity"
      subtitle="Every run of the agent: what it read, what it changed in Plane, and why."
      actions={
        <Button variant="outline" disabled={!connected || runCycle.isPending} onClick={() => runCycle.mutate()}>
          Run a cycle now
        </Button>
      }
    >
      {!connected && <NotConnected />}
      {q.error && <p className="text-sm text-red-500">{q.error.message}</p>}

      <section aria-label="Last 24 hours" className="grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))] gap-px overflow-hidden rounded-md bg-[#2A2927]">
        {[
          [s?.runs, "runs in 24 h", ""],
          [s?.failed, "failed runs", s?.failed ? "text-[#F48A80]" : "text-[#7FD39B]"],
          [s?.changes, "Plane changes proposed", ""],
          [s?.refused, s?.refused === 1 ? "guard refusal" : "guard refusals", s?.refused ? "text-[#F0B65A]" : ""],
        ].map(([n, label, color]) => (
          <div key={String(label)} className="flex flex-col gap-0.5 bg-[#1A1A19] px-5 py-4">
            <span className={`font-mono text-[22px] font-bold ${color}`}>{n ?? "–"}</span>
            <span className="text-[13px] text-[#A8A59D]">{label}</span>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Kind</span>
          <Select className={`${control} min-w-40`} value={search.mode ?? ""} onChange={(e) => set("mode", e.target.value)}>
            <option value="">All runs</option>
            <option value="apply">15-min cycle (apply)</option>
            <option value="suggest">15-min cycle (suggest)</option>
            <option value="sweep">Morning sweep</option>
            <option value="catchup">Catch-up</option>
            <option value="backfill">History import</option>
            <option value="approved">Approved suggestions</option>
          </Select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Status</span>
          <Select className={`${control} min-w-36`} value={search.status ?? ""} onChange={(e) => set("status", e.target.value)}>
            <option value="">Any status</option>
            <option value="ok">ok</option>
            <option value="failed">failed</option>
            <option value="running">running</option>
          </Select>
        </label>
        <form
          className="flex flex-[1_1_240px] flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Find</span>
          <Input className={control} type="search" aria-label="Find a run or task" placeholder="A run id or a task key, e.g. ACME-14" value={find} onChange={(e) => setFind(e.target.value)} />
        </form>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[880px] border-collapse">
          <thead>
            <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
              <th className={th}>Run</th>
              <th className={th}>Kind</th>
              <th className={th}>Status</th>
              <th className={th}>Started</th>
              <th className={`${th} text-right`}>Read</th>
              <th className={`${th} text-right`}>Changes</th>
              <th className={`${th} text-right`}>Cost*</th>
              <th className={th}>Summary</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {(q.data?.runs ?? []).map((r) => (
              <tr key={r.id} className="border-t border-[#2A2927]">
                <td className={`${td} font-mono`}>
                  <Link to="/servers/$id/runs/$rid" params={{ id, rid: String(r.id) }}>
                    {num(r.id)}
                  </Link>
                </td>
                <td className={`${td} text-[#CFCBC1]`}>{KIND[r.mode] ?? r.mode}</td>
                <td className={td}>
                  <Chip value={r.status} />
                </td>
                <td className={`${td} text-[#A8A59D]`} title={took(r) === "–" ? undefined : `took ${took(r)}`}>
                  {when(r.started_at)}
                </td>
                <td className={`${td} text-right font-mono`}>{r.event_count}</td>
                <td className={`${td} text-right font-mono`}>{changes(r)}</td>
                <td className={`${td} text-right font-mono`}>{money(r.cost)}</td>
                <td className={`${td} ${summaryTone(r)}`}>{r.summary ?? (r.status === "running" ? "Running…" : "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {q.data && q.data.runs.length === 0 && <p className="px-5 py-4 text-sm text-[#A8A59D]">No runs match.</p>}
        {!q.data && !q.error && connected && <p className="px-5 py-4 text-sm text-[#A8A59D]">Loading…</p>}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-xs text-[#A8A59D]">
          * API-equivalent cost reported by Claude Code. With a subscription, runs are covered by your plan. "?" = proposed only (suggest mode).
        </p>
        {q.data?.more && (
          <Link
            to="/servers/$id/activity"
            params={{ id }}
            search={{ ...search, before: q.data.runs[q.data.runs.length - 1]?.id }}
            className="font-mono text-[13px] no-underline"
          >
            Older runs →
          </Link>
        )}
      </div>
    </ServerPage>
  );
}
